import * as React from 'react';
import { ISupportCategory } from '../../../models/Support/ISupportCategory';
import { useUpdateCategoryMutation } from '../../../queries/Support/useSupportCategories';

type UpdateCategoryMutation = ReturnType<typeof useUpdateCategoryMutation>;

interface CategoryRowDragProps {
	draggable: true;
	onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
	onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
	onDragLeave: () => void;
	onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
	onDragEnd: () => void;
}

interface CategoryDropIndicator {
	above: boolean;
	below: boolean;
}

/**
 * Owns the drag-to-reorder machinery for the categories list so CategoriesTab's CRUD flow stays
 * readable. The optimistic `localOrder` is held and released only once every reorder PUT has
 * settled — releasing per `data` change snapped the list to a partially-persisted order mid-drag.
 */
export function useCategoryReorder(serverCategories: ISupportCategory[], updateMutation: UpdateCategoryMutation) {
	const [dragIndex, setDragIndex] = React.useState<number | null>(null);
	const [dropIndex, setDropIndex] = React.useState<number | null>(null);
	const [dropPosition, setDropPosition] = React.useState<'above' | 'below'>('below');
	const [localOrder, setLocalOrder] = React.useState<ISupportCategory[] | null>(null);
	const [reorderError, setReorderError] = React.useState(false);

	const orderedCategories = localOrder ?? serverCategories;

	const persistReorder = async (fromIndex: number, toIndex: number) => {
		if (fromIndex === toIndex) return;
		const reordered = [...orderedCategories];
		const [moved] = reordered.splice(fromIndex, 1);
		reordered.splice(toIndex, 0, moved);

		// Optimistic: update UI immediately.
		setLocalOrder(reordered.map((cat, i) => ({ ...cat, sortOrder: i })));
		setReorderError(false);

		// Persist only items whose position actually changed.
		const changed = reordered.map((cat, index) => ({ cat, index })).filter(({ cat, index }) => cat.sortOrder !== index);

		try {
			await Promise.all(
				changed.map(({ cat, index }) =>
					updateMutation.mutateAsync({
						id: cat.id,
						name: cat.name,
						description: cat.description,
						aiGuidance: cat.aiGuidance,
						color: cat.color,
						isActive: cat.isActive,
						sortOrder: index,
					})
				)
			);
		} catch {
			// A PUT failed — surface it; the finally block resyncs to whatever persisted.
			setReorderError(true);
		} finally {
			// Release the optimistic order only after every PUT settled, so the list
			// reflects the final server truth rather than a partial intermediate order.
			setLocalOrder(null);
		}
	};

	const getRowDragProps = (index: number): CategoryRowDragProps => ({
		draggable: true,
		onDragStart: e => {
			setDragIndex(index);
			e.dataTransfer.effectAllowed = 'move';
		},
		onDragOver: e => {
			e.preventDefault();
			e.dataTransfer.dropEffect = 'move';
			const rect = e.currentTarget.getBoundingClientRect();
			const midY = rect.top + rect.height / 2;
			setDropPosition(e.clientY < midY ? 'above' : 'below');
			setDropIndex(index);
		},
		onDragLeave: () => setDropIndex(null),
		onDrop: e => {
			e.preventDefault();
			if (dragIndex !== null && dropIndex !== null) {
				const insertAt = dropPosition === 'above' ? dropIndex : dropIndex + 1;
				const adjustedInsert = dragIndex < insertAt ? insertAt - 1 : insertAt;
				persistReorder(dragIndex, adjustedInsert);
			}
			setDragIndex(null);
			setDropIndex(null);
		},
		onDragEnd: () => {
			setDragIndex(null);
			setDropIndex(null);
		},
	});

	const dropIndicator = (index: number): CategoryDropIndicator => ({
		above: dragIndex !== null && dropIndex === index && dropPosition === 'above',
		below: dragIndex !== null && dropIndex === index && dropPosition === 'below',
	});

	return { orderedCategories, reorderError, getRowDragProps, dropIndicator };
}
